import React, { useState } from 'react';

import {
    ActivityStatus, StudyChannel, TrackingData, TestKind, fmtDate, fmtDuration, fmtHours,
} from './types';

const STATUS_STYLES: Record<string, { bg: string; fg: string; label: string }> = {
    completed:   { bg: '#e3f3e8', fg: '#005c20', label: 'Completed' },
    in_progress: { bg: '#fff3d6', fg: '#a07000', label: 'In progress' },
    incomplete:  { bg: '#fff3d6', fg: '#a07000', label: 'Incomplete' },
    not_started: { bg: '#F1F5F9', fg: '#667085', label: 'Not started' },
};

function StatusPill({ status }: { status: ActivityStatus }) {
    const s = STATUS_STYLES[status] || STATUS_STYLES.not_started;
    return <span className='r-status' style={{ background: s.bg, color: s.fg }}>{s.label}</span>;
}

function StudyTime({ data, hideVocab, testsOnly }: { data: TrackingData; hideVocab: boolean; testsOnly: boolean }) {
    const parts: StudyChannel[] = testsOnly
        ? [data.study.test]
        : hideVocab
        ? [data.study.test, data.study.assignments]
        : [data.study.test, data.study.assignments, data.study.vocab];
    const total = parts.reduce((a, p) => a + p.min, 0);
    const hasEstimate = (data.study.test.estimatedMin ?? 0) > 0;
    const splitText = testsOnly
        ? 'on practice tests'
        : hideVocab ? 'across practice tests and assignments' : 'across practice tests, assignments, and vocab';

    return (
        <div className='r-card'>
            <div className='r-card__head'><div>
                <div className='r-card__title'>Study Time</div>
                <div className='r-card__sub'>
                    Total time {splitText}. Sessions are logged from July 2026 onward;
                    earlier test time is estimated from question-level activity{hasEstimate ? ' (marked ~)' : ''}.
                </div>
            </div></div>
            <div className='r-time'>
                <div>
                    <div className='r-kpi__label'>Total logged</div>
                    <div className='r-time__total'>{hasEstimate ? '~' : ''}{fmtHours(total)}</div>
                </div>
            </div>
            {total > 0
                ? <div className='r-time__seg'>
                    {parts.map(p => (
                        <div key={p.label} className='r-time__cell' style={{ width: `${(p.min / total) * 100}%`, background: p.color }}>
                            {p.min / total > 0.1 ? `${Math.round((p.min / total) * 100)}%` : ''}
                        </div>
                    ))}
                </div>
                : <div className='r-emptycard' style={{ padding: '14px 0 4px', textAlign: 'left' }}>No study time logged yet.</div>
            }
            <div className='r-legend'>
                {parts.map(p => (
                    <div key={p.label} className='r-legend__item'>
                        <span className='r-legend__k'><span className='r-dot' style={{ background: p.color }} />{p.label}</span>
                        <span className='r-legend__v'>{(p.estimatedMin ?? 0) > 0 ? '~' : ''}{fmtHours(p.min)}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

type TableColumn = {
    key: string;
    header: string;
    width: string;
    align?: 'left' | 'right';
    render: (row: any) => React.ReactNode;
};

function ActivityTable({ columns, rows, emptyMsg }: { columns: TableColumn[]; rows: any[]; emptyMsg: string }) {
    if (!rows.length) {
        return <div className='r-emptycard'>{emptyMsg}</div>;
    }
    const template = columns.map(c => c.width).join(' ');
    return (
        <div className='r-tbl'>
            <div className='r-tr r-tr--h' style={{ gridTemplateColumns: template }}>
                {columns.map(c => <div key={c.key} style={{ textAlign: c.align || 'left' }}>{c.header}</div>)}
            </div>
            {rows.map((r, i) => (
                <div className='r-tr' key={r.id ?? i} style={{ gridTemplateColumns: template }}>
                    {columns.map(c => (
                        <div key={c.key} style={{ textAlign: c.align || 'left', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {c.render(r)}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}

function Coverage({ data, hideVocab, testKind }: { data: TrackingData; hideVocab: boolean; testKind: TestKind }) {
    const [open, setOpen] = useState<string | null>(null);
    const toggle = (key: string) => setOpen(prev => (prev === key ? null : key));

    const testsOnly = testKind !== 'sat';
    const cards: { key: string; label: string; color: string; counts: { assigned: number; completed: number } }[] = testsOnly
        ? [{ key: 'tests', label: 'Tests', color: '#1C2434', counts: data.counts.tests }]
        : [
            { key: 'tests', label: 'Tests', color: '#1C2434', counts: data.counts.tests },
            { key: 'assignments', label: 'Assignments', color: '#0575a2', counts: data.counts.assignments },
            ...(hideVocab ? [] : [{ key: 'vocab', label: 'Vocab Sets', color: '#a23205', counts: data.counts.vocab }]),
        ];

    let detail: React.ReactNode = null;
    if (open === 'tests') {
        // ACT has no RW/Math split — show just the composite total.
        const satScoreCols: TableColumn[] = [
            { key: 'rw', header: 'R&W', width: '60px', align: 'right', render: r => <span className='r-num' style={{ color: '#0575a2' }}>{r.scoreRW ?? '—'}</span> },
            { key: 'm', header: 'Math', width: '60px', align: 'right', render: r => <span className='r-num' style={{ color: '#a23205' }}>{r.scoreM ?? '—'}</span> },
        ];
        detail = <ActivityTable emptyMsg='No tests assigned yet.' rows={data.testsList} columns={[
            { key: 'name', header: 'Test', width: '1.6fr', render: r => r.name },
            { key: 'assigned', header: 'Assigned', width: '80px', render: r => <span className='r-num' style={{ color: '#667085' }}>{fmtDate(r.assignedDate)}</span> },
            { key: 'total', header: testKind === 'act' ? 'Composite' : 'Total', width: '80px', align: 'right', render: r => <span className='r-num' style={{ fontWeight: 700 }}>{r.total ?? '—'}</span> },
            ...(testsOnly ? [] : satScoreCols),
            { key: 'status', header: 'Status', width: '100px', render: r => <StatusPill status={r.status} /> },
            { key: 'dur', header: 'Duration', width: '70px', align: 'right', render: r => <span className='r-num' style={{ color: '#667085' }} title={r.durationEstimated ? 'Estimated from question-level activity' : undefined}>{r.durationMin != null && r.durationEstimated ? '~' : ''}{fmtDuration(r.durationMin)}</span> },
        ]} />;
    }
    if (open === 'assignments') {
        detail = <ActivityTable emptyMsg='No assignments yet.' rows={data.assignmentsList} columns={[
            { key: 'name', header: 'Assignment', width: '1.8fr', render: r => (
                <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</div>
                    {r.sectionLabel && <div className='r-skill__dom'>{r.sectionLabel}</div>}
                </div>
            ) },
            { key: 'assigned', header: 'Assigned', width: '90px', render: r => <span className='r-num' style={{ color: '#667085' }}>{fmtDate(r.assignedDate)}</span> },
            { key: 'score', header: 'Score', width: '70px', align: 'right', render: r => <span className='r-num' style={{ fontWeight: 700 }}>{r.score != null ? `${r.score}%` : '—'}</span> },
            { key: 'status', header: 'Status', width: '110px', render: r => <StatusPill status={r.status} /> },
            { key: 'dur', header: 'Duration', width: '70px', align: 'right', render: r => <span className='r-num' style={{ color: '#667085' }}>{fmtDuration(r.durationMin)}</span> },
        ]} />;
    }
    if (open === 'vocab') {
        detail = <ActivityTable emptyMsg='No vocab sets assigned yet.' rows={data.vocabList} columns={[
            { key: 'name', header: 'Vocab Set', width: '1.8fr', render: r => <span style={{ fontWeight: 600 }}>{r.name}</span> },
            { key: 'assigned', header: 'Assigned', width: '90px', render: r => <span className='r-num' style={{ color: '#667085' }}>{fmtDate(r.assignedDate)}</span> },
            { key: 'quiz', header: 'Quiz', width: '80px', render: r => <span style={{ color: r.quiz ? '#1C2434' : '#98a2b3' }}>{r.quiz ?? '—'}</span> },
            { key: 'quizScore', header: 'Score', width: '80px', align: 'right', render: r => <span className='r-num' style={{ fontWeight: 700 }}>{r.quizScore != null ? `${r.quizScore}%` : '—'}</span> },
            { key: 'status', header: 'Status', width: '110px', render: r => <StatusPill status={r.status} /> },
        ]} />;
    }

    return (
        <div className='r-card'>
            <div className='r-card__head'><div>
                <div className='r-card__title'>Assigned vs Completed</div>
                <div className='r-card__sub'>Coverage across each channel. Click a card to see the full list.</div>
            </div></div>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cards.length}, minmax(0, 1fr))`, gap: 14 }}>
                {cards.map(c => {
                    const pct = c.counts.assigned ? c.counts.completed / c.counts.assigned : 0;
                    const selected = open === c.key;
                    return (
                        <div
                            key={c.key}
                            className={selected ? 'r-cov r-cov--on' : 'r-cov'}
                            onClick={() => toggle(c.key)}
                            role='button'
                            tabIndex={0}
                            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(c.key); } }}
                        >
                            <div className='r-cov__top'>
                                <span className='r-cov__lab'>{c.label}</span>
                                <span className='r-cov__view'>{selected ? 'Hide ▲' : 'View ▼'}</span>
                            </div>
                            <div className='r-cov__big'>
                                {c.counts.completed}
                                <span style={{ fontSize: 15, color: '#828282', fontWeight: 600 }}> / {c.counts.assigned}</span>
                            </div>
                            <div className='r-cov__bar'><div className='r-cov__fill' style={{ width: `${pct * 100}%`, background: c.color }} /></div>
                            <div className='r-cov__pct'>
                                <span><b style={{ color: '#005c20' }}>{Math.round(pct * 100)}%</b> complete</span>
                                <span>{c.counts.assigned - c.counts.completed} left</span>
                            </div>
                        </div>
                    );
                })}
            </div>
            {detail}
        </div>
    );
}

function StudyTracking({ data, hideVocab, testKind }: { data: TrackingData; hideVocab: boolean; testKind: TestKind }) {
    const testsOnly = testKind !== 'sat';
    return (
        <div className='r-wrap' style={{ paddingTop: 0, gap: 18 }}>
            <StudyTime data={data} hideVocab={hideVocab} testsOnly={testsOnly} />
            <Coverage data={data} hideVocab={hideVocab} testKind={testKind} />
        </div>
    );
}

export default StudyTracking;
